import React, { useState, useEffect, useRef } from "react";
import { Button, Offcanvas, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FileUploader } from "./FileUploader";
import cx from "classnames";

import moment from "../vendor/moment";

const READ_ALL_FILES = "/escuela/archivos/json";
import { filesize } from "filesize";

const mimeToIcon = {
  "application/zip": "fa-file-archive",
  "application/x-7z-compressed": "fa-file-archive",
  "application/msword": "fa-file-word",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "fa-file-word", // .docx
  "application/vnd.oasis.opendocument.text": "fa-file-word", // .odt
  "application/rtf": "fa-file-word", // .rtf
  "application/pdf": "fa-file-pdf",
  "application/vnd.ms-powerpoint": "fa-file-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "fa-file-powerpoint", // .pptx
  "application/vnd.openxmlformats-officedocument.presentationml.slideshow":
    "fa-file-powerpoint", // .ppsx
  "application/vnd.ms-excel": "fa-file-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    "fa-file-excel", // .xlsx
  "application/x-mspublisher": "fa-file", // .pub (no exact icon)
  "application/vnd.android.package-archive": "fa-android", // .apk
  "application/x-cdr": "fa-vector-square", // .cdr (best fit)
  "application/xspf+xml": "fa-list", // .xspf playlist
  "application/aia": "fa-cubes", // .aia (App Inventor) — project file
  "application/vnd.sb2": "fa-cube", // .sb2 (Scratch 2)
  "application/vnd.sb3": "fa-cube", // .sb3 (Scratch 3)
  "application/octet-stream": "fa-file", // Generic binary: .spbq, .wve, etc.
  "application/x-shockwave-flash": "fa-bolt", // Flash
  "image/jpeg": "fa-file-image",
  "image/png": "fa-file-image",
  "image/gif": "fa-file-image",
  "image/x-ms-bmp": "fa-file-image",
  "image/webp": "fa-file-image",
  "audio/mpeg": "fa-file-audio",
  "audio/x-wav": "fa-file-audio",
  "audio/ogg": "fa-file-audio",
  "audio/mp4": "fa-file-audio", // .m4a
  "video/mp4": "fa-file-video",
  "model/stl": "fa-cube", // .stl 3D model
  "text/html": "fa-file-code", // .ht
  "text/x-python": "fa-file-code",
};
const Icon = ({ file }) => (
  <i className={cx("me-1 fas", mimeToIcon[file.mime_type] || "fa-file")} />
);

const FileBadge = ({ file, onRemove }) => {
  return (
    <div className="btn-group mb-2 me-2" key={file.id}>
      <button className="btn btn-sm btn-outline-secondary " type="button">
        <Icon file={file} />
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`tooltip-${file.id}`}>
              {file.filename}
              <br />
              {file.size}
              <br />
              {new Date(file.created_at).toLocaleString()}
            </Tooltip>
          }
        >
          <span>{file.filename}</span>
        </OverlayTrigger>
        <input type="hidden" name="attachments" value={file.id} />
      </button>
      <button
        className="btn btn-sm btn-outline-danger"
        type="button"
        onClick={onRemove}
      >
        <i className="fas fa-trash te" />
      </button>
    </div>
  );
};

export const AttachementOffcanvasList = () => {
  const [groupedFiles, setGroupedFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const target = useRef(null);

  const fetchArchivos = async () => {
    setLoading(true);
    const response = await fetch(READ_ALL_FILES);
    const periods = await response.json();
    setGroupedFiles(periods);
    setLoading(false);
  };

  useEffect(() => {
    // Step 1: Get the script element by ID
    const scriptTag = document.getElementById("attachments-list-initial");
    if (scriptTag) {
      // Step 2: Get its text content
      const jsonText = scriptTag.textContent || scriptTag.innerHTML;

      // Step 3: Parse the JSON
      let data;
      try {
        setSelectedFiles(JSON.parse(jsonText));
      } catch (error) {
        console.error("Failed to parse JSON:", error);
      }
    }
    fetchArchivos();
  }, []);

  const handleAddFile = (file) => (ev) => {
    ev.preventDefault();
    setSelectedFiles([...selectedFiles, file]);
  };
  const handleRemoveFile = (file) => (ev) => {
    ev.preventDefault();
    setSelectedFiles(selectedFiles.filter((f) => f.id !== file.id));
  };

  const handleClose = () => setShow(false);

  const handleShow = () => {
    fetchArchivos();
    setShow(true);
  };

  const handleFileUploader = (files) => {
    setSelectedFiles([...selectedFiles, ...files]);
  };

  return (
    <>
      <FileUploader onChange={handleFileUploader} />
      <div ref={target} id="attachments-list">
        {selectedFiles.map((file) => (
          <FileBadge
            key={file.id}
            file={file}
            onRemove={handleRemoveFile(file)}
          />
        ))}
      </div>

      <Button
        className="btn btn-secondary"
        variant="light"
        onClick={handleShow}
      >
        <i className="fas fa-paperclip" /> Adjuntar archivos...
      </Button>

      <Offcanvas show={show} onHide={handleClose} placement="start">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Archivos</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {isLoading ? (
            <div className="d-flex justify-content-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
            </div>
          ) : (
            <div className="list-group">
              {groupedFiles.map(([groupName, files]) => (
                <React.Fragment key={groupName}>
                  {files.length > 0 && (
                    <a
                      href="#"
                      className="list-group-item list-group-item-action"
                      aria-current="true"
                    >
                      <div className="d-flex w-100 justify-content-between">
                        <h5 className="mb-1">{groupName}</h5>
                      </div>
                    </a>
                  )}
                  {files.map((file) => {
                    const selected = selectedFiles.find(
                      (f) => f.id === file.id
                    );

                    return (
                      <a
                        href="#"
                        key={file.id}
                        className={cx(
                          "list-group-item list-group-item-action",
                          selected && "active"
                        )}
                        onClick={!selected ? handleAddFile(file) : undefined}
                      >
                        <div className="d-flex w-100 justify-content-between">
                          <h6 className="mb-1">
                            <Icon file={file} />
                            {file.filename}
                          </h6>
                          <small className="text-nowrap">
                            {filesize(file.size)}
                          </small>
                        </div>
                        <small className="text-nowrap">
                          {moment(file.created_at).fromNow()}
                        </small>
                      </a>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};
